import { createSelector } from 'reselect';

const _getMenuCategories = (state) => state.businessApp.menu.categories;
const _getMenuItems = (state) => state.businessApp.menu.items;
const _getMenuTranslationsDictionary = (state) => state.businessApp.menu.translationDictionary;
const _getMenuInfo = (state) => state.businessApp.menu.editInfo;
const getRestaurants = state => state.businessApp.restaurants;
const getRawMenu = state => state.businessApp.menu;

export const getPrimaryCategories = createSelector(
    [_getMenuCategories, _getMenuItems],
    (categories, items) => {
        const categoriesList = Object.values(categories);

        function prepareCategoryItem(instance) {
            if (!items[instance.itemId]) console.log(instance);
            const { price: originalPrice, posItemId: originalPosItemId, ...item } = items[instance.itemId];
            const subcategories = item.subcategoriesIds.map(sid => ({ ...categories[sid] }));
            let branchOverrides = null;
            if (item.branchOverrides || instance.branchOverrides) branchOverrides = { ...item.branchOverrides, ...instance.branchOverrides };

            return ({ ...item, ...instance, branchOverrides, originalPrice, originalPosItemId, subcategories });
        }

        function prepareCategory(category) {
            const categoryChilds = categoriesList.filter(c => c.primaryParentId === category.id);
            const isPrimaryParent = categoryChilds.length > 0;

            if (isPrimaryParent) {
                return {
                    ...category,
                    isPrimaryParent,
                    categories: categoryChilds.map(prepareCategory).sort((a, b) => a.sortIndex - b.sortIndex),
                    subcategories: [],
                    items: []
                };
            }

            return {
                ...category,
                subcategories: category.subcategoriesIds.map(sid => ({ ...categories[sid] })),
                items: category.itemsInstances.map(prepareCategoryItem)
            };
        }

        return categoriesList
            .filter(category => category.type === 'primary' && !category.primaryParentId)
            .map(prepareCategory)
            .sort((a, b) => a.sortIndex - b.sortIndex);
    }
);

export const getSubcategories = createSelector(
    [_getMenuCategories, _getMenuItems],
    (categories, items) => {
        const categoriesList = Object.values(categories);

        function prepareCategoryItem(instance) {
            const { price: originalPrice, posItemId: originalPosItemId, ...item } = items[instance.itemId];
            const subcategories = item.subcategoriesIds.map(sid => ({ ...categories[sid] }));

            return ({ ...item, ...instance, originalPrice, originalPosItemId, subcategories });
        }

        function prepareCategory(category) {
            return {
                ...category,
                subcategories: [],
                items: category.itemsInstances.map(prepareCategoryItem)
            };
        }

        return categoriesList.filter(category => category.type !== 'primary').map(prepareCategory);
    }
);

export const getMenuItemsAppearances = createSelector(
    [_getMenuCategories],
    (categories) => {
        return Object.values(categories).reduce((results, category) => {
            category.itemsInstances.forEach(({ itemId }) => {
                if (!results[itemId]) results[itemId] = [];
                results[itemId].push(`[${category.key}] ${category.name}`);
            });
            return results;
        }, {});
    }
);

export const getMenuSubcategoriesAppearances = createSelector(
    [_getMenuCategories, _getMenuItems],
    (categories, items) => {
        const appearances = {};

        Object.values(categories).forEach(category => {
            category.subcategoriesIds.forEach(subId => {
                if (!appearances[subId]) appearances[subId] = [];
                appearances[subId].push(`C-${category.key} ${category.name}`);
            });
        });

        Object.values(items).forEach(item => {
            item.subcategoriesIds.forEach(subId => {
                if (!appearances[subId]) appearances[subId] = [];
                appearances[subId].push(`I-${item.key} ${item.name}`);
            });
        });

        return appearances;
    }
);

export const getMenuTranslationsTable = createSelector(
    [_getMenuTranslationsDictionary],
    (dictionary) => {
        const results = [];

        Object.keys(dictionary).forEach(lang => {
            Object.keys(dictionary[lang]).forEach(key => {
                const row = results.find(r => r.main === key);

                if (row) {
                    row[lang] = dictionary[lang][key];
                } else {
                    results.push({
                        main: key,
                        [lang]: dictionary[lang][key],
                    });
                }
            });
        });

        return results;
    }
);

export const getMenuInfo = createSelector(
    [_getMenuInfo, getRestaurants],
    (info, restaurants) => {
        let linkedBranches = info.linkedBranches || [];
        const isLinkedToMany = linkedBranches.length > 1;

        linkedBranches = restaurants
            .filter(rest => linkedBranches.includes(rest.id))
            .map(rest => rest.name);

        return { ...info, linkedBranches, isLinkedToMany };
    }
);

export const getMenu = createSelector(
    [getPrimaryCategories, getSubcategories, getMenuInfo, _getMenuCategories, _getMenuItems],
    (primaryCategories, subcategories, info, allCategoriesDict, allItemsDict) => {
        return {
            primaryCategories,
            subcategories,
            info,
            allCategoriesDict,
            allItemsDict,
        };
    }
);

export const getPreparedMenu = createSelector(
    [getRawMenu],
    (menu) => {
        return menu.preparedMenu.menuDictionary 
            ? { 
                ...menu.preparedMenu.menuDictionary,
                categories: Object.values(menu.preparedMenu.menuDictionary.categories)
            }
            : {};
    }
);
